import React, { useEffect, useRef } from 'react';
import { Info, Upload, AlertTriangle } from 'lucide-react';
import {
  DomainCard, StatusBadge, StatValue, SectionHeading, EmptyState, Skeleton,
  Tooltip, TooltipTrigger, TooltipContent, ICON_PX, ICON_STROKE, useToast,
} from '../ui';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';
import { mockConsumerSurveys } from '../../utils/mockData';
import { addToPriorityQueue } from '../../utils/api';

function ConsumerSurveysTab({ readOnly }) {
  const { selectedUnitId } = useConsumerUnit();
  const data = mockConsumerSurveys[selectedUnitId] || mockConsumerSurveys.all;
  const { toast } = useToast();
  const queuedRef = useRef(false);

  useEffect(() => {
    if (readOnly || queuedRef.current) return;
    const breached = data.domains.filter((d) => d.score < d.mipsThreshold);
    if (breached.length === 0) return;
    queuedRef.current = true;
    breached.forEach((d) => {
      addToPriorityQueue({
        title: `MIPS Below Threshold: ${d.label} (${d.score} < ${d.mipsThreshold})`,
        priority: 'high', category: 'Consumer', type: 'task',
        estimatedTime: 30, dueDate: new Date().toISOString(),
      }).catch(() => {});
    });
    toast({ title: `${breached.length} MIPS breach(es) added to Priority Queue` });
  }, [data.domains, readOnly, toast]);

  if (!data) return <Skeleton.KPICard size="md" />;

  return (
    <div className="flex flex-col gap-3">
      {!readOnly && (
        <DomainCard domain="consumer" accentPlacement="top" className="flex flex-col items-center justify-center py-4 border-dashed border-2 border-nn-border">
          <Upload size={ICON_PX.xl} className="text-text-muted mb-2" strokeWidth={ICON_STROKE} />
          <p className="text-body text-text-muted mb-2">Drop CAHPS CSV here or click to browse</p>
          <div className="flex gap-2">
            <StatusBadge variant="info">Press Ganey</StatusBadge>
            <StatusBadge variant="info">SurveyVitals</StatusBadge>
            <StatusBadge variant="info">PRC</StatusBadge>
          </div>
        </DomainCard>
      )}

      <div>
        <SectionHeading
          size="sm" className="mb-1.5" title="Domain Scores"
          action={
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
              </TooltipTrigger>
              <TooltipContent side="bottom" maxWidth={220}>CAHPS domain scores from latest upload. Red accent = below MIPS threshold.</TooltipContent>
            </Tooltip>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {data.domains.map((d) => {
            const belowMips = d.score < d.mipsThreshold;
            const belowBenchmark = d.score < d.benchmark;
            return (
              <DomainCard
                key={d.key} domain="consumer" accentPlacement="left"
                alertLevel={belowMips ? 'critical' : belowBenchmark ? 'warning' : undefined}
              >
                <div className="flex items-center justify-between">
                  <span className="text-heading-sm font-bold text-text-dark">{d.label}</span>
                  {belowMips && (
                    <StatusBadge variant="critical">
                      <AlertTriangle size={ICON_PX.xs} className="mr-0.5" /> Below MIPS
                    </StatusBadge>
                  )}
                </div>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="text-[28px] font-bold text-text-dark">{d.score}</span>
                  <span className="text-caption text-text-muted">Benchmark: {d.benchmark}</span>
                  <span className="text-caption text-text-muted">MIPS: {d.mipsThreshold}</span>
                  <StatValue value={d.trend} unit=" QoQ" />
                </div>
              </DomainCard>
            );
          })}
        </div>
      </div>

      <div>
        <SectionHeading
          size="sm" className="mb-1.5" title="Upload History"
          action={
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
              </TooltipTrigger>
              <TooltipContent side="bottom" maxWidth={220}>Past CAHPS CSV uploads with parse status and row counts.</TooltipContent>
            </Tooltip>
          }
        />
        {data.uploadHistory.length === 0 ? (
          <EmptyState title="No uploads yet" body="Upload a CAHPS CSV to see survey results." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-body">
              <thead>
                <tr className="text-caption text-text-muted border-b border-nn-border">
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Vendor</th>
                  <th className="text-left py-2 px-2">Period</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Rows</th>
                  {selectedUnitId === 'all' && <th className="text-left py-2 px-2">Unit</th>}
                </tr>
              </thead>
              <tbody>
                {data.uploadHistory.map((u) => (
                  <tr key={u.id} className="border-b border-nn-border">
                    <td className="py-2 px-2">{u.date}</td>
                    <td className="py-2 px-2">{u.vendor}</td>
                    <td className="py-2 px-2">{u.period}</td>
                    <td className="py-2 px-2">
                      <StatusBadge variant={u.status === 'parsed' ? 'on-track' : 'critical'}>{u.status}</StatusBadge>
                    </td>
                    <td className="py-2 px-2 text-right">{u.rowCount}</td>
                    {selectedUnitId === 'all' && <td className="py-2 px-2 text-text-muted">{u.unitName}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsumerSurveysTab;
